import { Department } from './apis/departments';
import { FileMetadata } from './apis/files';
import { UserRole } from './apis/users';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: string;
  status: 'operational' | 'beta' | 'maintenance';
  icon: string;
  features: string[];
  users: string[];
  metrics: {
    [key: string]: string;
  };
  url?: string;
  slackChannels?: string[];
  contactPerson?: {
    name: string;
    email: string;
    role: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TutorialStep {
  id: string;
  title: string;
  content: string;
  type: 'text' | 'video' | 'code' | 'quiz' | 'pdf';
  order: number;
  videoUrl?: string;
  pdfUrl?: string;
  pdfFile?: File | null;
  questions?: QuizQuestion[];
}

export interface QuizQuestion {
  id: string;
  question: string;
  title: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface Tutorial {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  category: string;
  product?: string | null;
  language?: 'en' | 'es';
  steps: TutorialStep[];
  authorId: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  avatarFile?: FileMetadata | null;
  avatarFileId?: string | null;
  position?: string;
  positionDescription?: string;
  departmentId?: string;
  department?: Department | null;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  role?: 'user' | 'admin';
}

export interface AuthResponse {
  user: User;
  token: string;
}

class ApiService {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.token = localStorage.getItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, any> } = {},
  ): Promise<ApiResponse<T>> {
    let url = `${this.baseURL}${endpoint}`;

    // Handle query parameters
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // For now, use a hardcoded admin token for testing
    // TODO: Implement proper authentication flow
    const adminToken =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiJhZG1pbi0xIiwiaWF0IjoxNzU4NTI5MTA5LCJleHAiOjE3NTkxMzM5MDl9.xlXy4XEZ5M2NOuqZJaPynQXxZ2NAkuicppZT8BCwvG0';
    (headers as any).Authorization = `Bearer ${adminToken}`;

    try {
      // Remove params from options since we've already processed them
      const { params, ...fetchOptions } = options;
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        // Create an error object that preserves the response data
        const error = new Error(data.error || `HTTP error! status: ${response.status}`);
        (error as any).response = { data };
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.data) {
      this.token = response.data.token;
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data!;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.data) {
      this.token = response.data.token;
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data!;
  }

  async auth0Callback(token: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/callback', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });

    if (response.data) {
      this.token = response.data.token;
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data!;
  }

  async getProfile(): Promise<User> {
    const response = await this.request<User>('/auth/profile');
    return response.data!;
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Product methods

  async getProducts(
    _page: number = 1,
    _limit: number = 10,
  ): Promise<{
    products: Product[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Product[]>('/products', {
      method: 'GET',
    });

    return {
      products: response.data || [],
      pagination: response.pagination,
    };
  }

  async getProduct(id: string): Promise<Product> {
    const response = await this.request<Product>(`/products/${id}`);
    return response.data!;
  }

  async getProductsByCategory(
    category: string,
    _page: number = 1,
    _limit: number = 10,
  ): Promise<{
    products: Product[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Product[]>(`/products/category/${category}`, {
      method: 'GET',
    });

    return {
      products: response.data || [],
      pagination: response.pagination,
    };
  }

  async createProduct(productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<{
    success: boolean;
    data: Product;
    message: string;
  }> {
    const response = await this.request<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
    return response as {
      success: boolean;
      data: Product;
      message: string;
    };
  }

  async updateProduct(
    id: string,
    productData: Partial<Product>,
  ): Promise<{
    success: boolean;
    data: Product;
    message: string;
  }> {
    const response = await this.request<Product>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
    return response as {
      success: boolean;
      data: Product;
      message: string;
    };
  }

  async deleteProduct(id: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.request<null>(`/products/${id}`, {
      method: 'DELETE',
    });
    return response as {
      success: boolean;
      message: string;
    };
  }

  async bulkDeleteProducts(ids: string[]): Promise<{
    success: boolean;
    message: string;
    data?: {
      deleted: number;
      failed: string[];
    };
  }> {
    const response = await this.request<{
      deleted: number;
      failed: string[];
    }>('/products/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
    return response as {
      success: boolean;
      message: string;
      data?: {
        deleted: number;
        failed: string[];
      };
    };
  }

  // Tutorial methods
  async getTutorials(
    page: number = 1,
    limit: number = 10,
    language: string = 'en',
  ): Promise<{
    tutorials: Tutorial[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Tutorial[]>('/tutorials', {
      method: 'GET',
      params: { page, limit, language },
    });

    return {
      tutorials: response.data || [],
      pagination: response.pagination,
    };
  }

  async getTutorial(id: string): Promise<Tutorial> {
    const response = await this.request<Tutorial>(`/tutorials/${id}`);
    return response.data!;
  }

  // Alias for getTutorial
  async getTutorialById(id: string): Promise<Tutorial> {
    return this.getTutorial(id);
  }

  async getTutorialsByCategory(
    category: string,
    _page: number = 1,
    _limit: number = 10,
  ): Promise<{
    tutorials: Tutorial[];
    pagination: ApiResponse['pagination'];
  }> {
    const response = await this.request<Tutorial[]>(`/tutorials/category/${category}`, {
      method: 'GET',
    });

    return {
      tutorials: response.data || [],
      pagination: response.pagination,
    };
  }

  async createTutorial(tutorial: Omit<Tutorial, 'id' | 'createdAt' | 'updatedAt' | 'authorId'>): Promise<Tutorial> {
    const response = await this.request<Tutorial>('/tutorials', {
      method: 'POST',
      body: JSON.stringify(tutorial),
    });

    return response.data!;
  }

  async updateTutorial(id: string, tutorial: Partial<Tutorial>): Promise<Tutorial> {
    const response = await this.request<Tutorial>(`/tutorials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tutorial),
    });

    return response.data!;
  }

  async deleteTutorial(id: string): Promise<void> {
    await this.request(`/tutorials/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteTutorials(ids: string[]): Promise<{
    success: boolean;
    message: string;
    data?: {
      deleted: number;
      failed: string[];
    };
  }> {
    const response = await this.request<{
      deleted: number;
      failed: string[];
    }>('/tutorials/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
    return response as {
      success: boolean;
      message: string;
      data?: {
        deleted: number;
        failed: string[];
      };
    };
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export types for use in components
// Types are already exported above
